import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Play, Pause, Share2, ArrowLeft, Instagram, Twitter, Youtube, Facebook, Music as MusicIcon, ExternalLink } from "lucide-react";
import { api, imgUrl } from "../lib/api";
import { useMediaPlayer } from "../lib/mediaPlayer";
import { slugify } from "../lib/slug";
import { share, trackShareUrl, artistShareUrl } from "../lib/share";
import Seo from "../components/Seo";

const SOCIAL_DEFS = [
  { key: "instagram_url", label: "Instagram", Icon: Instagram, color: "#E4405F" },
  { key: "twitter_url", label: "X / Twitter", Icon: Twitter, color: "#FFFFFF" },
  { key: "youtube_url", label: "YouTube", Icon: Youtube, color: "#FF0033" },
  { key: "spotify_url", label: "Spotify", Icon: MusicIcon, color: "#1DB954" },
  { key: "apple_music_url", label: "Apple Music", Icon: MusicIcon, color: "#FA243C" },
  { key: "tiktok_url", label: "TikTok", Icon: MusicIcon, color: "#FFFFFF" },
  { key: "soundcloud_url", label: "SoundCloud", Icon: MusicIcon, color: "#FF5500" },
  { key: "facebook_url", label: "Facebook", Icon: Facebook, color: "#1877F2" },
  { key: "website_url", label: "Website", Icon: ExternalLink, color: "#FCA311" },
];

// Same regex used by mediaPlayer/FamRadioPlayer — one source of truth would
// be nicer but we keep this local to avoid extra import cycles.
const ARTIST_SPLIT_RE = /\s*(?:,|&|\+|\bx\b|\bfeat\.?\b|\bft\.?\b|\bwith\b|\bvs\.?\b)\s*/i;
const splitArtists = (raw) => (raw ? raw.split(ARTIST_SPLIT_RE).map((s) => s.trim()).filter(Boolean) : []);

const SITE = "https://syndafam.com";

export default function ArtistDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const p = useMediaPlayer();
  const [artists, setArtists] = useState([]);
  const [tracks, setTracks] = useState([]);
  // Gallery photos tagged with this artist — surfaced on the profile so a
  // fan sees behind-the-scenes shots, show pics, promo drops, etc. Fetched
  // once we know the slug (which comes from the URL param, not the artist
  // record — so we can start the request before /artists resolves).
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/artists").then((r) => setArtists(r.data || [])).catch(() => {}),
      api.get("/playlist").then((r) => setTracks(r.data || [])).catch(() => {}),
      api.get(`/gallery/by-artist/${encodeURIComponent(slug)}`).then((r) => setPhotos(r.data || [])).catch(() => setPhotos([])),
    ]).finally(() => setLoading(false));
  }, [slug]);

  const artist = useMemo(
    () => artists.find((a) => slugify(a.name) === slug) || null,
    [artists, slug]
  );

  const artistTracks = useMemo(() => {
    if (!artist) return [];
    const target = artist.name.trim().toLowerCase();
    return tracks.filter((t) => {
      const names = splitArtists(t.artist || "").map((n) => n.toLowerCase());
      return names.includes(target) || (t.artist || "").trim().toLowerCase() === target;
    });
  }, [artist, tracks]);

  const socials = artist ? SOCIAL_DEFS.filter((s) => artist[s.key]) : [];

  const trackShare = (t) => {
    share({
      title: t.title || "Untitled",
      text: `${t.title || "Untitled"}${t.artist ? ` — ${t.artist}` : ""} · SyndaFam`,
      url: trackShareUrl(t.id),
    });
  };

  const shareArtist = () => {
    if (!artist) return;
    share({
      title: artist.name,
      text: `${artist.name} — ${artist.role || "Artist"} · SyndaFam`,
      url: artistShareUrl(slugify(artist.name)),
    });
  };

  // Loading / not-found states
  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center text-white/40 font-mono text-xs tracking-widest" data-testid="artist-detail-loading">
        LOADING PROFILE…
      </div>
    );
  }
  if (!artist) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center text-center px-6" data-testid="artist-detail-notfound">
        <Seo title="Artist Not Found | SyndaFam" description="This SyndaFam artist profile could not be found. Browse the full Toronto hip-hop roster instead." path={`/artists/${slug}`} noindex />
        <div className="font-display text-5xl mb-4">ARTIST NOT FOUND.</div>
        <p className="text-white/60 max-w-md mb-8">This profile might have moved. Head back to the roster to find every SyndaFam artist.</p>
        <Link to="/artists" className="inline-flex items-center gap-2 bg-[#E63946] hover:bg-[#C92A36] text-white font-mono text-xs tracking-[0.3em] px-6 py-3">
          <ArrowLeft className="w-4 h-4" /> BACK TO ROSTER
        </Link>
      </div>
    );
  }

  const canonicalPath = `/artists/${slugify(artist.name)}`;
  const title = `${artist.name} | ${artist.role || "Artist"} · SyndaFam`;
  const description = (artist.bio || `${artist.name} — ${artist.role || "artist"} in the SyndaFam Toronto hip-hop collective. Listen to tracks, watch videos, and follow across every platform.`)
    .replace(/\s+/g, " ")
    .slice(0, 158);
  const artistSlug = slugify(artist.name);
  // Dedicated 1200×630 Open Graph card generated on the backend so shares to
  // iMessage / Instagram / X / Facebook unfurl with the artist's face. Served
  // as JPEG for maximum Meta / Instagram Messenger compatibility.
  const ogImage = `${SITE}/api/og/artist/${artistSlug}.jpg`;
  const imageAbs = artist.photo_path
    ? (imgUrl(artist.photo_path).startsWith("http") ? imgUrl(artist.photo_path) : `${SITE}${imgUrl(artist.photo_path)}`)
    : `${SITE}/og-image.jpg`;

  // Rich JSON-LD: Person + affiliation to MusicGroup SyndaFam + sameAs socials +
  // list of released tracks as MusicRecording. This is the payload that AI
  // Overview crawlers use to surface individual members.
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: artist.name,
    url: `${SITE}${canonicalPath}`,
    image: imageAbs,
    description: artist.bio || `${artist.name} is a ${artist.role || "artist"} in the SyndaFam Toronto hip-hop collective.`,
    jobTitle: artist.role || "Artist",
    affiliation: {
      "@type": "MusicGroup",
      name: "SyndaFam",
      url: `${SITE}/`,
    },
    memberOf: {
      "@type": "MusicGroup",
      name: "SyndaFam",
      url: `${SITE}/`,
    },
    sameAs: socials.map((s) => artist[s.key]).filter(Boolean),
    ...(artistTracks.length > 0 && {
      subjectOf: artistTracks.slice(0, 30).map((t) => ({
        "@type": "MusicRecording",
        name: t.title,
        url: `${SITE}/?track=${t.id}`,
        byArtist: { "@type": "MusicGroup", name: artist.name },
      })),
    }),
  };

  return (
    <div className="relative bg-[#0A0A0A] text-white overflow-x-hidden" data-testid="artist-detail-page">
      <Seo
        title={title.slice(0, 60)}
        description={description}
        path={canonicalPath}
        image={ogImage}
        imageAlt={`${artist.name} — SyndaFam artist`}
        jsonLd={jsonLd}
      />

      {/* Living aurora + drifting orbs */}
      <div className="fixed inset-0 -z-10 bg-aurora pointer-events-none opacity-70" aria-hidden="true" />
      <div className="fixed inset-0 -z-10 pointer-events-none" aria-hidden="true">
        <div className="orb orb-red orb-live"   style={{ width: 620, height: 620, top: "-160px", left: "-180px" }} />
        <div className="orb orb-amber orb-live" style={{ width: 520, height: 520, bottom: "-160px", right: "-140px", animationDelay: "-6s" }} />
        <div className="orb orb-cyan orb-live"  style={{ width: 360, height: 360, top: "40%", left: "40%", opacity: 0.18, animationDelay: "-3s" }} />
      </div>

      <style>{`
        @keyframes pulse-glow-red {
          0%, 100% { box-shadow: 0 0 30px #E63946, 0 0 80px #E6394655; }
          50%      { box-shadow: 0 0 55px #E63946, 0 0 130px #E6394699; }
        }
      `}</style>

      {/* HERO */}
      <section className="relative">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-10 pt-16 pb-10">
          {/* Breadcrumb back link */}
          <Link
            to="/artists"
            className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.4em] text-white/50 hover:text-[#FCA311] transition-colors mb-8"
            data-testid="artist-detail-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> ROSTER
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-10 lg:gap-14 items-center">
            {/* Avatar */}
            <div
              className="relative rounded-full overflow-hidden mx-auto lg:mx-0 flex-shrink-0"
              style={{
                width: 320,
                height: 320,
                maxWidth: "80vw",
                maxHeight: "80vw",
                border: "3px solid #E63946",
                animation: "pulse-glow-red 2.6s ease-in-out infinite",
              }}
              data-testid="artist-detail-avatar"
            >
              {artist.photo_path ? (
                <img src={imgUrl(artist.photo_path)} alt={artist.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center font-display text-[9rem] text-white/20 bg-black">
                  {artist.name.slice(0, 1).toUpperCase()}
                </div>
              )}
            </div>

            {/* Meta + socials + CTA */}
            <div className="min-w-0">
              <div className="font-mono text-[10px] tracking-[0.5em] text-[#FCA311]" data-testid="artist-detail-role">
                {(artist.role || "ARTIST").toUpperCase()}
              </div>
              <h1
                className="font-display leading-[0.85] tracking-tighter mt-2"
                style={{ fontSize: "clamp(3rem, 8vw, 6.5rem)" }}
                data-testid="artist-detail-name"
              >
                {artist.name}<span className="text-[#E63946] text-glow-red">.</span>
              </h1>

              {artist.bio && (
                <p className="mt-6 text-white/75 text-base sm:text-lg leading-relaxed max-w-2xl whitespace-pre-wrap" data-testid="artist-detail-bio">
                  {artist.bio}
                </p>
              )}

              <div className="mt-8 flex flex-wrap items-center gap-3">
                {socials.map(({ key, label, Icon, color }) => (
                  <a
                    key={key}
                    href={artist[key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    title={label}
                    className="w-11 h-11 rounded-full bg-black/70 backdrop-blur flex items-center justify-center transition-transform hover:scale-110"
                    style={{
                      border: `2px solid ${color}`,
                      boxShadow: `0 0 14px ${color}55, inset 0 0 6px ${color}22`,
                    }}
                    data-testid={`artist-detail-social-${key}`}
                  >
                    <Icon className="w-4 h-4" style={{ color }} />
                  </a>
                ))}
                {socials.length === 0 && (
                  <span className="font-mono text-[10px] tracking-widest text-white/40">NO LINKS YET — STAY TUNED.</span>
                )}

                <button
                  onClick={shareArtist}
                  className="ml-1 inline-flex items-center gap-2 border-2 border-white/15 hover:border-[#FCA311] hover:text-[#FCA311] text-white/80 font-mono text-[10px] tracking-[0.35em] px-4 py-2.5 transition-colors"
                  data-testid="artist-detail-share"
                  aria-label="Copy profile link"
                >
                  <Share2 className="w-3.5 h-3.5" /> SHARE PROFILE
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY — photos admin tagged with this artist. Silently hides
          when nothing is tagged so no empty state clutters the page. */}
      {photos.length > 0 && (
        <section className="max-w-[1200px] mx-auto px-5 sm:px-10 pb-16" data-testid="artist-detail-gallery">
          <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
            <div>
              <div className="font-mono text-[10px] tracking-[0.4em] text-[#FCA311]">// GALLERY</div>
              <h2 className="font-display text-3xl sm:text-4xl mt-1">{artist.name.toUpperCase()} · FRAMES</h2>
            </div>
            <div className="font-mono text-[10px] tracking-widest text-white/40">
              {photos.length} PHOTO{photos.length === 1 ? "" : "S"}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {photos.map((ph) => (
              <Link
                key={ph.id}
                to={`/gallery?photo=${ph.id}`}
                className="group relative block aspect-square overflow-hidden glass-card"
                data-testid={`artist-gallery-photo-${ph.id}`}
              >
                <img
                  src={imgUrl(ph.image_path)}
                  alt={ph.caption || `${artist.name} photo`}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                {ph.caption && (
                  <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/90 via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="font-mono text-[10px] tracking-widest text-white/90 truncate">{ph.caption}</div>
                  </div>
                )}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* TRACKS */}
      <section className="max-w-[1200px] mx-auto px-5 sm:px-10 pb-24">
        <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
          <div>
            <div className="font-mono text-[10px] tracking-[0.4em] text-[#E63946]">// CATALOGUE</div>
            <h2 className="font-display text-3xl sm:text-4xl mt-1">TRACKS FEATURING {artist.name.toUpperCase()}</h2>
          </div>
          <div className="font-mono text-[10px] tracking-widest text-white/40">
            {artistTracks.length} TRACK{artistTracks.length === 1 ? "" : "S"}
          </div>
        </div>

        {artistTracks.length === 0 ? (
          <div className="glass-card rounded-2xl p-10 text-center font-mono text-xs tracking-widest text-white/40" data-testid="artist-detail-tracks-empty">
            NO TRACKS TAGGED YET — CHECK BACK SOON.
          </div>
        ) : (
          <div className="glass-card-strong rounded-2xl overflow-hidden" data-testid="artist-detail-tracks">
            {artistTracks.map((t, i) => {
              const active = t.id === p.currentTrack?.id;
              const isPlaying = active && p.playing;
              return (
                <div
                  key={t.id}
                  className={`group grid grid-cols-[44px_1fr_auto] items-center gap-3 px-4 sm:px-5 py-3 border-b border-white/5 last:border-b-0 transition-colors ${active ? "bg-[#E63946]/15 shadow-[inset_2px_0_0_#E63946]" : "hover:bg-white/[0.04]"}`}
                  data-testid={`artist-track-row-${t.id}`}
                >
                  <button
                    onClick={() => (isPlaying ? p.togglePlay() : p.playTrack(t.id))}
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${active ? "bg-[#E63946] text-white" : "bg-white/5 hover:bg-[#E63946] text-white/80 hover:text-white"}`}
                    aria-label={isPlaying ? `Pause ${t.title}` : `Play ${t.title}`}
                    data-testid={`artist-track-play-${t.id}`}
                  >
                    {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                  </button>
                  <div className="min-w-0">
                    <div className={`font-display text-base sm:text-lg truncate ${active ? "text-white" : "text-white/90"}`}>
                      {t.title || "Untitled"}
                    </div>
                    {t.artist && (
                      <div className="font-mono text-[10px] tracking-widest text-white/45 truncate">
                        {t.artist.toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="hidden sm:inline font-mono text-[9px] tracking-widest text-white/30 uppercase mr-2">
                      {t.media_type || "audio"}
                    </span>
                    <button
                      onClick={() => trackShare(t)}
                      className="w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center text-white/60 hover:text-[#FCA311] transition-colors"
                      aria-label={`Copy share link for ${t.title}`}
                      title="Copy share link"
                      data-testid={`artist-track-share-${t.id}`}
                    >
                      <Share2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-10">
          <button
            onClick={() => navigate("/artists")}
            className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.4em] text-white/60 hover:text-[#FCA311] transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> BACK TO FULL ROSTER
          </button>
        </div>
      </section>
    </div>
  );
}
